import { z } from "zod";

import { VibeTemplateDefaultEnvVarsSchema } from "../../../../schemas/VibeTemplate";
import { VibeBuilderKindReadSchema } from "./enum-read.schemas";

/**
 * VibeTemplate identity. Slug is the CLI handle and the public route
 * segment — same shape constraints as VibeApp.name (DNS-label safe)
 * so it round-trips cleanly between argv, URL segments, and S3
 * prefixes when a template is forked into an app.
 *   - lowercase letters, digits, hyphen
 *   - must start with a letter
 *   - 1–63 chars (DNS label cap)
 */
export const VibeTemplateSlugSchema = z
  .string()
  .trim()
  .min(1, "slug is required")
  .max(63, "slug must be ≤ 63 characters")
  .regex(
    /^[a-z][a-z0-9-]*$/,
    "slug must start with a lowercase letter and contain only lowercase letters, digits, and hyphens"
  );

/**
 * Read shape for a VibeTemplate returned to the CLI / catalog browse.
 * The `published` field is INTENTIONALLY exposed — once a row is in
 * the public-list response it's published by definition, but the
 * field carries through so admin reads (later slice) can render the
 * same DTO without divergence. Customer-facing surfaces never see
 * `published: false` rows because the read adapters filter by it.
 */
export const VibeTemplateSchema = z.object({
  id: z.string().uuid(),
  slug: VibeTemplateSlugSchema,
  name: z.string(),
  description: z.string(),
  kind: VibeBuilderKindReadSchema,
  gitUrl: z.string().url(),
  defaultEnvVars: VibeTemplateDefaultEnvVarsSchema,
  published: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeTemplateDto = z.infer<typeof VibeTemplateSchema>;

// ---------------------------------------------------------------------------
// ListPublishedVibeTemplates — `GET /api/vibe/templates`
// ---------------------------------------------------------------------------

export const ListPublishedVibeTemplatesResponseSchema = z.object({
  templates: z.array(VibeTemplateSchema)
});

// ---------------------------------------------------------------------------
// GetVibeTemplate — `GET /api/vibe/templates/:slug`
// ---------------------------------------------------------------------------

export const GetVibeTemplatePathVarsSchema = z.object({
  slug: VibeTemplateSlugSchema
});

export const GetVibeTemplateResponseSchema = z.object({
  template: VibeTemplateSchema
});
